<template>
  <section class="container-fluid system-operations">
    <PageHeader showBreadcrumbs page-title="System operations">
      <template #buttons>
        <div class="d-flex align-items-center">
          <label for="input-days" class="m-0">Days: </label>
          <b-form-input v-model="days" size="lg" id="input-days" class="btn__input"></b-form-input>
        </div>
        <b-button
          class="button title-container__controls-item align-items-center"
          :variant="'info'"
          :disabled="isLoading"
          @click="syncWorklogs(days)"
        >
          <span class="material-icons icon-image-preview">
            sync
          </span>
          Sync worklogs ({{ days }})
        </b-button>
        <b-button
          class="button title-container__controls-item align-items-center"
          :variant="'info'"
          :disabled="isLoading"
          @click="syncIssues(days)"
        >
          <span class="material-icons icon-image-preview">
            sync
          </span>
          Sync issues ({{ days }})
        </b-button>
        <b-button
          class="button title-container__controls-item align-items-center"
          :variant="'info'"
          :disabled="isLoading"
          @click="checkUsers()"
        >
          <span class="material-icons icon-image-preview">
            sync
          </span>
          Check jira users
        </b-button>
      </template>
    </PageHeader>

    <div class="mb-2">
      <span v-if="syncStatus==='success'" class="text-success">
        <span class="material-icons icon-image-preview">
              check
        </span>
        Synced successfully
      </span>
      <span v-if="syncStatus==='error'" class="text-danger">
        <span class="material-icons icon-image-preview">
              error
        </span>
        Synced with error
      </span>
    </div>
    <div v-if="isLoading && !isInitialDataLoaded" class="content-loading-wrapper" style="min-height: 60vh;">
      <Preloader class="centered-preloader large-spinner" />
    </div>

    <div v-else class="full-height-content">
      <TableForPage
        v-if="tableData.length"
        :isDataLoading="isLoading && isInitialDataLoaded"
      style="margin-bottom: 0;"
      :tableData="tableData"
      :fields="fields"
      :slots="slots"
      :perPage="perPage"
      :currentPage="currentPage"
      :totalRows="totalRows"
      isServerRender
      @update="fetchData"
    >
      <template v-slot:operation_status="slotProps">
        <span :class="{'text-danger': slotProps.item.operation_status === 'error'}">{{ slotProps.item.operation_status }}</span>
      </template>
      <template v-slot:date="slotProps">
        <div>Start: {{ $moment(slotProps.item.created_at, "YYYY-MM-DD hh:mm:ss").format('DD MMMM YY, h:mm:ss a') }}</div>
        <div>End: {{ $moment(slotProps.item.updated_at, "YYYY-MM-DD hh:mm:ss").format('DD MMMM YY, h:mm:ss a') }}</div>
      </template>
      <template v-slot:details="{ item }">
        <div v-if="item.operation_type === 'check_jira_users'">
          <div v-if="item.details.response && item.details.response.changed_name">
            <h6>Changed name in Jira</h6>
            <TableForPage
              :tableData="item.details.response ? item.details.response.changed_name : []"
              :fields="usersChangedNameFields"
              :slots="slotsNestedChangedName"
              :hide-part-table="'200px'"
              :per-page="20"
              :sticky-table-head="false"
              :table-id="`table-user-${item.id}`"
            >
              <template v-slot:sync="slotNestedProps">
                <span class="text-success d-flex align-items-center" v-if="slotNestedProps.item.jira_name_from_db === slotNestedProps.item.jira_name_from_api">
                  <span class="material-icons icon-image-preview">check</span>Synced
                </span>
                <span v-else>
                  <b-button
                    class="button title-container__controls-item align-items-center"
                    :variant="'info'"
                    @click="syncUser(item.id, slotNestedProps.item.email, slotNestedProps.item.jira_name_from_api)"
                  >
                    <span class="material-icons icon-image-preview">
                      sync
                    </span>
                    Sync
                  </b-button>
                </span>
              </template>
            </TableForPage>
          </div>
          <div v-if="item.details.response && item.details.response.unavailable_email" class="mt-4">
            <h6>Unavailable email in Jira</h6>
            <TableForPage
              :tableData="sortActiveFirst(item.details.response ? item.details.response.unavailable_email : null)"
              :fields="usersUnavailableEmailFields"
              :slots="slotsNestedUnavailableEmail"
              :hide-part-table="'200px'"
              :current-page="nestedTableCurrentPage"
              :per-page="nestedTablePerPage"
              :totalRows="totalNestedTableUSers"
              :is-show-pagination="true"
              :is-current-first-page="false"
              :sticky-table-head="false"
              @update="updateUsersUnavailableEmail"
            >
              <template v-slot:name="slotNestedProps">
                {{ slotNestedProps.item.name }}<span v-if="slotNestedProps.item.inactive" class="text-dark"> (inactive)</span>
              </template>
            </TableForPage>
          </div>
        </div>
        <json-viewer
          v-else
          :value="item.details"
          :expand-depth=5
          boxed
        ></json-viewer>
      </template>
    </TableForPage>
    <EmptyStateNoResults
      v-else-if="isInitialDataLoaded && !tableData.length"
      title="No system operations yet"
      description="No system operations have been performed yet. Use the sync buttons above to start operations."
      customIcon="settings"
      customIconClasses="material-symbols-outlined empty-state__icon"
      :withoutButton="true"
    />
    </div>
  </section>
</template>

<script>
import TableForPage from '@tables/TableForPage'
import JsonViewer from 'vue-json-viewer'
import axios from 'axios'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  components: {
    TableForPage,
    JsonViewer,
    PageHeader,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      days: '7',
      isLoading: false,
      isAllOptionsFilterSet: false,
      isSystemOperationsDataLoaded: false,
      syncStatus: '',
      syncMessage: '',
      currentPage: 1,
      perPage: null,
      totalRows: 0,
      tableData: [],
      fields: [
        {
          key: 'operation_type',
          label: 'Type',
          class: 'col-md-1'
        },
        {
          key: 'operation_status',
          label: 'Status',
          class: 'col-md-1'
        },
        {
          key: 'date',
          label: 'Date',
          class: 'col-md-2'
        },
        {
          key: 'details',
          label: 'Details'
        }
      ],
      slots: {
        operation_status: {
          name: 'operation_status'
        },
        date: {
          name: 'date'
        },
        details: {
          name: 'details'
        }
      },
      usersChangedNameFields: [
        {
          key: 'email',
          label: 'Email',
          class: 'col-md-2'
        },
        {
          key: 'name',
          label: 'user name',
          class: 'col-md-3'
        },
        {
          key: 'jira_name_from_db',
          label: 'Jira name in DB',
          class: 'col-md-2'
        },
        {
          key: 'jira_name_from_api',
          label: 'Jira name in Jira API',
          class: 'col-md-2'
        },
        {
          key: 'sync',
          label: 'Action'
        }
      ],
      slotsNestedChangedName: {
        sync: {
          name: 'sync'
        }
      },
      slotsNestedUnavailableEmail: {
        name: {
          name: 'name'
        }
      },
      usersUnavailableEmailFields: [
        {
          key: 'email',
          label: 'Email',
          class: 'col-md-3'
        },
        {
          key: 'name',
          label: 'user name',
          class: 'col-md-3'
        },
        {
          key: 'jira_name_from_db',
          label: 'Jira name in DB',
          class: 'col-md-3'
        },
        {
          key: 'comment',
          label: 'comment',
          class: 'col-md-3'
        }
      ],
      nestedTableCurrentPage: 1,
      nestedTablePerPage: 15,
      totalNestedTableUSers: null
    }
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isSystemOperationsDataLoaded
    }
  },
  created () {
    this.isAllOptionsFilterSet = true
    this.fetchData({ currentPage: this.currentPage })
  },
  methods: {
    syncWorklogs (days) {
      this.syncSystemOperations('sync-worklogs', { days })
    },
    syncIssues (days) {
      this.syncSystemOperations('sync-issues', { days })
    },
    checkUsers () {
      this.syncStatus = ''
      this.syncSystemOperations('check-users')
    },
    syncUser (operationId, email, newUserName) {
      this.syncStatus = ''
      this.syncSystemOperations('sync-user-name', { operation_id: operationId, email, new_user_name: newUserName })
    },
    syncSystemOperations (operation, params = {}) {
      this.isLoading = true
      this.syncStatus = ''
      this.axios
        .post(
          `/api/system-operations/${operation}/`,
          params,
          {
            headers: {
              Authorization: 'Bearer' + this.$store.getters.getAccessToken
            }
          }
        )
        .then(response => {
          this.syncStatus = response.data.status
        })
        .catch(({ response }) => {
          this.syncStatus = response.data.message.status
        })
        .finally(() => {
          this.isLoading = false
          this.currentPage = 1
          this.fetchData({ currentPage: this.currentPage })
        })
    },
    fetchData (data) {
      this.isLoading = true
      const { currentPage } = data
      axios
        .get(`/api/system-operations?page=${currentPage}`, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(
          ({ data }) => {
            this.perPage = data.per_page
            this.currentPage = data.current_page
            this.totalRows = data.total
            this.tableData = data.data
          },
          console.error
        )
        .finally(() => {
          this.isLoading = false
          this.isSystemOperationsDataLoaded = true
        })
    },
    sortActiveFirst (users) {
      if (users) {
        this.totalNestedTableUSers = Object.values(users).length
        if (this.nestedTableCurrentPage && this.nestedTableCurrentPage === 1) {
          return Object.values(users).sort((a, b) => a.inactive - b.inactive).slice(0, this.nestedTablePerPage)
        } else {
          return Object.values(users).sort((a, b) => a.inactive - b.inactive).slice(this.nestedTablePerPage * (this.nestedTableCurrentPage - 1), this.nestedTablePerPage * this.nestedTableCurrentPage)
        }
      }
      return []
    },
    updateUsersUnavailableEmail (data) {
      if (!data) {
        return ''
      }

      this.nestedTableCurrentPage = data.currentPage
      this.nestedTablePerPage = data.perPage
    }
  }
}
</script>

<style lang="scss" scopes>
  .system-operations {
    .btn__input {
      max-width: 40px;
      min-height: 20px;
      margin: 0 5px;
      padding: 0 5px;
    }
  }
</style>
